import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * ImageLightbox - Full-screen image gallery modal
 * Uses React Portal to render outside parent DOM hierarchy
 * This ensures it always covers the full screen regardless of parent styles
 */
const ImageLightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance for navigation
  const minSwipeDistance = 50;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onNext, onPrev]);

  // Touch handlers for swipe navigation
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && images.length > 1) {
      onNext();
    }
    if (isRightSwipe && images.length > 1) {
      onPrev();
    }
  };

  if (!images || images.length === 0) return null;

  // Lightbox content
  const lightboxContent = (
    <div 
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[10000] text-white hover:text-[var(--color-primary)] transition-colors p-2"
        aria-label="Close gallery"
      >
        <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Image counter */}
      <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-[10000] text-white bg-black/50 px-3 py-1 sm:px-4 sm:py-2 rounded-full">
        <span className="text-xs sm:text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </span>
      </div>

      {/* Previous button - visible on all screens but smaller on mobile */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-2 sm:left-4 z-[10000] text-white hover:text-[var(--color-primary)] transition-colors p-1.5 sm:p-3 bg-black/50 rounded-full hover:bg-black/75 flex items-center justify-center"
          aria-label="Previous image"
        >
          <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Main image - FULL SCREEN */}
      <div 
        className="w-full h-full flex items-center justify-center p-2 sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />
      </div>

      {/* Next button - visible on all screens but smaller on mobile */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-2 sm:right-4 z-[10000] text-white hover:text-[var(--color-primary)] transition-colors p-1.5 sm:p-3 bg-black/50 rounded-full hover:bg-black/75 flex items-center justify-center"
          aria-label="Next image"
        >
          <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Thumbnail strip at bottom - only on larger screens */}
      {images.length > 1 && (
        <div className="hidden sm:block absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[10000] max-w-[90vw] overflow-x-auto">
          <div className="flex gap-2 px-4 py-2 bg-black/50 rounded-lg">
            {images.map((img, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  const steps = index - currentIndex;
                  if (steps > 0) {
                    for (let i = 0; i < steps; i++) onNext();
                  } else if (steps < 0) {
                    for (let i = 0; i < Math.abs(steps); i++) onPrev();
                  }
                }}
                className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-[var(--color-primary)] opacity-100' 
                    : 'border-transparent opacity-50 hover:opacity-75'
                }`}
              >
                <img
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Use React Portal to render lightbox at document.body level
  return createPortal(lightboxContent, document.body);
};

export default ImageLightbox;
